import { useEffect, useState } from "react";
import { CalendarHeart, CalendarDays, MapPin, Loader2 } from "lucide-react";
import { fetchPublic } from "../lib/content";
import { PageHero } from "../components/PageHero";

const TYPE_BADGE = {
  upcoming: { label: "Upcoming", bg: "bg-[#00B37E]", color: "text-white" },
  past: { label: "Past Event", bg: "bg-slate-200", color: "text-[#0A192F]" },
  celebration: { label: "Celebration", bg: "bg-amber-100", color: "text-amber-700" },
};

export default function Events() {
  const [events, setEvents] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([fetchPublic("events"), fetchPublic("holidays")])
      .then(([e, h]) => { setEvents(e); setHolidays(h); })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="events-page">
      <PageHero
        eyebrow="Events & Holidays"
        title="Celebrations, seminars and TASSEI life."
        desc="From rural awareness seminars to annual day celebrations — see what's happening, what's coming up, and our official holiday calendar."
        image="https://images.pexels.com/photos/787961/pexels-photo-787961.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1920"
        alt="TASSEI celebrations"
      />

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
          {/* Events */}
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3 mb-6">
              <CalendarHeart className="w-5 h-5 text-[#00B37E]" />
              <h2 className="font-heading text-2xl font-bold text-[#0A192F]">Events &amp; Celebrations</h2>
            </div>
            {loading ? (
              <div className="flex items-center gap-2 text-[#64748B]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : events.length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-2xl p-10 text-center text-[#64748B]">No events posted yet.</div>
            ) : (
              <div className="space-y-5" data-testid="events-list">
                {events.map((e, i) => {
                  const t = TYPE_BADGE[e.type] || TYPE_BADGE.upcoming;
                  return (
                    <article key={e.id} data-testid={`event-${i}`} className="bg-white border border-slate-200 rounded-2xl overflow-hidden md:flex hover:-translate-y-0.5 hover:shadow-lg transition-all">
                      {e.image ? (
                        <div className="md:w-56 md:flex-shrink-0 aspect-[16/9] md:aspect-auto overflow-hidden">
                          <img src={e.image} alt={e.title} className="w-full h-full object-cover" loading="lazy" />
                        </div>
                      ) : null}
                      <div className="p-5 md:p-6 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold ${t.bg} ${t.color}`}>{t.label}</span>
                          <span className="text-xs text-[#64748B] inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{e.date}</span>
                        </div>
                        <h3 className="font-heading text-lg font-bold text-[#0A192F] mt-2">{e.title}</h3>
                        {e.description ? <p className="text-sm text-[#475569] mt-2 leading-relaxed">{e.description}</p> : null}
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </div>

          {/* Holidays */}
          <aside className="lg:col-span-4">
            <div className="bg-[#0A192F] text-white rounded-2xl p-6 sticky top-24" data-testid="holidays-card">
              <div className="flex items-center gap-2 text-[#7CF0BD] text-xs uppercase tracking-[0.22em] font-bold">
                <MapPin className="w-3.5 h-3.5" /> Holiday Calendar
              </div>
              <h3 className="font-heading text-2xl font-extrabold mt-2">TASSEI {new Date().getFullYear()} Holidays</h3>
              {holidays.length === 0 ? (
                <p className="mt-4 text-white/70 text-sm">No holidays published yet.</p>
              ) : (
                <ul className="mt-5 space-y-3">
                  {holidays.map((h, i) => (
                    <li key={h.id} data-testid={`holiday-${i}`} className="flex items-start gap-3 border-b border-white/10 pb-3 last:border-0">
                      <div className="w-12 flex-shrink-0 text-center">
                        <div className="text-[10px] uppercase tracking-wider text-[#7CF0BD] font-bold">
                          {new Date(h.date).toLocaleDateString("en-IN", { month: "short" })}
                        </div>
                        <div className="font-heading text-xl font-extrabold">
                          {new Date(h.date).getDate()}
                        </div>
                      </div>
                      <div>
                        <div className="font-bold text-sm">{h.name}</div>
                        {h.description ? <div className="text-xs text-white/70 mt-0.5">{h.description}</div> : null}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}
